import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalTitle, ModalContent, ModalFooter, Button, MultiSelect, MultiSelectOption, ComboBox, ComboBoxOption } from "@utahdts/utah-design-system";
import "./siteVisits.css";
import { useQuery, useMutation } from "@apollo/client";
import { GET_WATERLINK_SITE_VISIT_TYPES, GET_ALL_EPA_SITE_VISIT_TYPES, ADD_WATERLINK_SITE_VISIT_TYPE } from "../../queries/siteVisitQrys/siteVisitTypesQrys";

const SiteVisitTypeModal = ({ isOpen, canEdit, onClose }) => {
  const [selectedEpaType, setSelectedEpaType] = useState(null);
  const [saveError, setSaveError] = useState("");
  const { data, loading, error, refetch: refetchWaterlinkTypes } = useQuery(GET_WATERLINK_SITE_VISIT_TYPES);
  const { data: epaTypes, loading: epaTypesLoading, error: epaTypesError, refetch: refetchEpaTypes } = useQuery(GET_ALL_EPA_SITE_VISIT_TYPES);
  const [addWaterlinkSiteVisitType, { loading: isSaving }] = useMutation(ADD_WATERLINK_SITE_VISIT_TYPE);
  const comboBoxPortalTarget = typeof document !== "undefined" ? document.body : null;
  const getSiteVisitTypeLabel = (type) => `${type.code} - ${type.name}`;
  const waterlinkSiteVisitTypes = useMemo(() => data?.getWaterlinkSiteVisitTypes || [], [data]);
  const siteVisitTypeOptions = useMemo(
    () => waterlinkSiteVisitTypes.filter((type) => typeof type?.name === "string" && type.name.length > 0),
    [waterlinkSiteVisitTypes]
  );
  const existingWaterlinkTypeCodes = useMemo(() => new Set(siteVisitTypeOptions.map((type) => type.code)), [siteVisitTypeOptions]);
  const epaSiteVisitTypeOptions = useMemo(
    () =>
      (epaTypes?.getAllEpaSiteVisitTypes || []).filter(
        (type) =>
          typeof type?.code === "string" &&
          type.code.length > 0 &&
          typeof type?.name === "string" &&
          type.name.length > 0 &&
          !existingWaterlinkTypeCodes.has(type.code)
      ),
    [epaTypes, existingWaterlinkTypeCodes]
  );

  useEffect(() => {
    if (isOpen) {
      refetchEpaTypes();
      refetchWaterlinkTypes();
    }
  }, [isOpen, refetchEpaTypes, refetchWaterlinkTypes]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!selectedEpaType) {
      return;
    }

    setSaveError("");

    try {
      const response = await addWaterlinkSiteVisitType({
        variables: {
          input: {
            code: selectedEpaType.code,
            name: selectedEpaType.name,
          },
        },
        errorPolicy: "all",
        refetchQueries: [
          { query: GET_WATERLINK_SITE_VISIT_TYPES },
          { query: GET_ALL_EPA_SITE_VISIT_TYPES },
        ],
        awaitRefetchQueries: true,
      });

      const responseErrorMessage = response?.errors?.[0]?.message;
      if (responseErrorMessage) {
        setSaveError(responseErrorMessage);
        return;
      }

      if (!response?.data?.addWaterlinkSiteVisitType) {
        setSaveError("Failed to save site visit type.");
        return;
      }

      onClose();
    } catch (mutationError) {
      setSaveError(mutationError?.message || "Failed to save site visit type.");
    }
  };

  return (
    <div className="utah-design-system wl-modal">
      <Modal
        className="modal--medium"
        onClose={onClose}
        onEscape={onClose}
      >
        <ModalTitle>New Site Visit Type</ModalTitle>
        <ModalContent>
          {loading && <p>Loading...</p>}
          {error && <p>Error loading site visit types</p>}
          {data && (
            <MultiSelect
              id="site-visit-types"
              label="Waterlink Site Visit Types"
              isDisabled
              values={siteVisitTypeOptions.map((type) => getSiteVisitTypeLabel(type))}
            >
              {siteVisitTypeOptions.map((type) => (
                <MultiSelectOption key={type.code} label={getSiteVisitTypeLabel(type)} value={getSiteVisitTypeLabel(type)}>
                  {getSiteVisitTypeLabel(type)}
                </MultiSelectOption>
              ))}
            </MultiSelect>
          )}
          {epaTypesLoading && <p>Loading EPA site visit types...</p>}
          {epaTypesError && <p>Error loading EPA site visit types</p>}
          {epaSiteVisitTypeOptions.length > 0 && (
            <ComboBox
              id="epa-site-visit-types"
              label="EPA Site Visit Types"
              ariaLabel="EPA Site Visit Types"
              autoComplete="off"
              firstSelectableByEnter={true}
              portalTarget={comboBoxPortalTarget}
              value={selectedEpaType ? getSiteVisitTypeLabel(selectedEpaType) : ""}
              onChange={(value) => {
                const selectedType = epaSiteVisitTypeOptions.find((type) => getSiteVisitTypeLabel(type) === value) || null;
                setSelectedEpaType(selectedType);
              }}
            >
              {epaSiteVisitTypeOptions.map((type) => (
                <ComboBoxOption key={getSiteVisitTypeLabel(type)} label={getSiteVisitTypeLabel(type)} value={getSiteVisitTypeLabel(type)}>
                  {getSiteVisitTypeLabel(type)}
                </ComboBoxOption>
              ))}
            </ComboBox>
          )}
        </ModalContent>
        <ModalFooter>
          {saveError && <p className="site-visits-save-error">{saveError}</p>}
          <Button onClick={onClose}>Cancel</Button>
          <Button appearance="solid" color="primary" isDisabled={!canEdit || isSaving || !selectedEpaType} onClick={handleSave}>Save</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SiteVisitTypeModal;
